import {
  CreateOnboardingOrganizationRequestDto,
  CreateOnboardingSetupRequestDto,
  CreateOnboardingUserRequestDto,
  GetOnboardingResponseDto,
  PatchOnboardingBrandingRequestDto,
} from "@/lib/interfaces/onboarding";
import { COMPANY_CATEGORIES, OnboardingFormData } from "./onboarding-schema";

const WORKING_TIME_MODEL_MAP: Record<string, string> = {
  vollzeit: "fullTime",
  teilzeit: "partTime",
  minijob: "miniJob",
  ausbildung: "apprenticeship",
};

type FrontendWorkingTimeModel =
  OnboardingFormData["locations"][number]["positions"][number]["belongsToWorkingTimeModels"][number];

const WORKING_TIME_MODEL_REVERSE_MAP: Record<string, FrontendWorkingTimeModel | undefined> = {
  fullTime: "vollzeit",
  partTime: "teilzeit",
  miniJob: "minijob",
  apprenticeship: "ausbildung",
};

function inferCategoriesFromDescription(description?: string): (typeof COMPANY_CATEGORIES)[number]["value"][] {
  if (!description) return [];
  const lower = description.toLowerCase();
  const matches = COMPANY_CATEGORIES.filter((category) => {
    const label = category.label.toLowerCase();
    return label.includes(lower) || lower.includes(label) || lower.includes(category.value.toLowerCase());
  }).map((category) => category.value);
  return matches.length > 0 ? matches : ["sonstiges"];
}

function splitCsv(value?: string): string[] {
  return (value ?? "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function toOnboardingUserDto(data: OnboardingFormData): CreateOnboardingUserRequestDto {
  return {
    firstName: data.firstName,
    lastName: data.lastName,
  };
}

export function toOnboardingOrganizationDto(data: OnboardingFormData): CreateOnboardingOrganizationRequestDto {
  return {
    name: data.companyName,
    ceo: data.ceo,
    street: data.street,
    postCode: data.postCode,
    placeName: data.placeName,
  };
}

export function toOnboardingSetupDto(data: OnboardingFormData): CreateOnboardingSetupRequestDto {
  return {
    employeeBenefitsTemplate: (data.benefits ?? []).join(", "),
    companyLocations: (data.locations ?? []).map((location) => ({
      title: location.name,
      description: (location.categories ?? [])
        .map((category) => COMPANY_CATEGORIES.find((entry) => entry.value === category)?.label)
        .filter(Boolean)
        .join(", "),
      street: location.street,
      postCode: location.postCode,
      placeName: location.placeName,
      applicantsContactName: location.contactName,
      applicantsContactPhone: location.contactPhone,
      applicantsContactMail: location.contactEmail,
      additionalContactMails: location.additionalEmails ?? [],
      jobOffers: (location.positions ?? []).map((position) => ({
        title: position.jobTitle,
        description: position.description,
        belongsToWorkingTimeModels: (position.belongsToWorkingTimeModels ?? [])
          .map((type) => WORKING_TIME_MODEL_MAP[type])
          .filter(Boolean),
        employeeBenefits: (position.benefits ?? []).join(", "),
        applicantProfile: position.applicantProfile,
        catchmentAreaKm: position.catchmentAreaKm,
      })),
    })),
  };
}

export function toOnboardingBrandingDto(data: OnboardingFormData): PatchOnboardingBrandingRequestDto {
  return {
    aboutUs: data.aboutUs,
    missionAndValues: data.missionAndValues,
    whyWorkWithUs: data.whyWorkWithUs,
    ciColorPrimary: data.ciColorPrimary,
  };
}

export function fromOnboardingResponseDto(response: GetOnboardingResponseDto): Partial<OnboardingFormData> {
  const user = response.user;
  const organization = response.organization;
  const companyLocations = response.companyLocations ?? [];
  const fallbackBenefitsFromJobOffers = companyLocations
    .flatMap((location) => location.jobOffers ?? [])
    .flatMap((jobOffer) => splitCsv(jobOffer.employeeBenefits));
  const uniqueFallbackBenefits = Array.from(new Set(fallbackBenefitsFromJobOffers));
  const employeeBenefitsTemplate = splitCsv(organization?.employeeBenefitsTemplate);

  return {
    firstName: user?.firstName ?? "",
    lastName: user?.lastName ?? "",
    companyName: organization?.name ?? "",
    ceo: organization?.ceo ?? "",
    street: organization?.street ?? "",
    postCode: organization?.postCode ?? "",
    placeName: organization?.placeName ?? "",
    aboutUs: organization?.aboutUs ?? "",
    missionAndValues: organization?.missionAndValues ?? "",
    whyWorkWithUs: organization?.whyWorkWithUs ?? "",
    benefits: employeeBenefitsTemplate.length > 0 ? employeeBenefitsTemplate : uniqueFallbackBenefits,
    ciColorPrimary: organization?.ciColorPrimary ?? "",
    logoFiles: organization?.logoUrl
      ? [
          {
            name: "organization-logo",
            size: 0,
            type: "image/*",
            previewUrl: organization.logoUrl,
          },
        ]
      : [],
    locations: companyLocations.map((location) => ({
      categories: inferCategoriesFromDescription(location.description),
      name: location.title ?? "",
      street: location.street ?? "",
      postCode: location.postCode ?? "",
      placeName: location.placeName ?? "",
      contactName: location.applicantsContactName ?? "",
      contactPhone: location.applicantsContactPhone ?? "",
      contactEmail: location.applicantsContactMail ?? "",
      additionalEmails: location.additionalContactMails ?? [],
      positions: (location.jobOffers ?? []).map((jobOffer) => ({
        jobTitle: jobOffer.title ?? "",
        belongsToWorkingTimeModels: (jobOffer.belongsToWorkingTimeModels ?? [])
          .map((value) => WORKING_TIME_MODEL_REVERSE_MAP[value])
          .filter((value): value is FrontendWorkingTimeModel => Boolean(value)),
        description: jobOffer.description ?? "",
        catchmentAreaKm: jobOffer.catchmentAreaKm ?? 25,
        benefits: splitCsv(jobOffer.employeeBenefits),
        applicantProfile: jobOffer.applicantProfile ?? "",
      })),
    })),
  };
}
